import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffAPI } from '../services/api';
import toast from '../utils/toast';

// Leave applications with filtering
export const useLeaveApplications = (filters = {}) => {
  return useQuery({
    queryKey: ['leave', 'applications', filters],
    queryFn: async () => {
      const response = await staffAPI.getLeaveApplications(filters);
      return response.data?.results || response.data || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - leave data changes frequently
  });
};

// Leave types (cached longer since they rarely change)
export const useLeaveTypes = () => {
  return useQuery({
    queryKey: ['leave', 'types'],
    queryFn: async () => {
      const response = await staffAPI.getAvailableLeaveTypes();
      return response.data?.results || response.data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - leave types rarely change
  });
};

export const useAnnualLeaveYearEndPlan = () => {
  return useQuery({
    queryKey: ['leave', 'yearEndPlan'],
    queryFn: async () => {
      const response = await staffAPI.getAnnualLeaveYearEndPlan();
      return response.data || { visible: false };
    },
    staleTime: 5 * 60 * 1000,
  });
};

// Active Home/Urgent Leave extension eligibility for the employee workflow.
// Keep this short-lived: timing, approvals, and pending requests can all change
// while the leave detail modal is open.
export const useLeaveExtensionEligibility = (applicationId, options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: ['leave', 'extension', 'eligibility', applicationId],
    queryFn: async () => {
      const response = await staffAPI.getExtensionEligibility(applicationId);
      return response.data;
    },
    enabled: Boolean(applicationId) && enabled,
    staleTime: 30 * 1000,
    retry: 1,
    ...queryOptions,
  });
};

// AL-first deduction preview for a proposed extension return date.
export const useLeaveExtensionPreview = (applicationId, newEndDate, options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: ['leave', 'extension', 'preview', applicationId, newEndDate],
    queryFn: async () => {
      const response = await staffAPI.getExtensionPreview(applicationId, newEndDate);
      return response.data;
    },
    enabled: Boolean(applicationId) && Boolean(newEndDate) && enabled,
    staleTime: 30 * 1000,
    retry: 1,
    ...queryOptions,
  });
};

export const useRequestLeaveExtension = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ applicationId, data }) => {
      const response = await staffAPI.requestExtension(applicationId, data);
      return response.data;
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['leave', 'extension', 'eligibility', variables.applicationId],
        }),
        queryClient.invalidateQueries({ queryKey: ['leave'] }),
      ]);
      toast.success('Extension request submitted. Awaiting approval.');
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.error
        || 'Failed to submit extension request. Please try again.'
      );
    },
  });
};

// Leave balances for current user
export const useMyLeaveBalances = (year = new Date().getFullYear()) => {
  return useQuery({
    queryKey: ['leave', 'balances', 'me', year],
    queryFn: async () => {
      const response = await staffAPI.getMyLeaveBalances({ year });
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// All leave balances (admin view)
export const useLeaveBalances = (params = {}) => {
  return useQuery({
    queryKey: ['leave', 'balances', params],
    queryFn: async () => {
      const response = await staffAPI.getLeaveBalances(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Leave statistics
export const useLeaveStatistics = (params = {}) => {
  return useQuery({
    queryKey: ['leave', 'statistics', params],
    queryFn: async () => {
      const response = await staffAPI.getLeaveStatistics(params);
      return response.data || {};
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Pending approvals for reporting officers
export const usePendingLeaveApprovals = () => {
  return useQuery({
    queryKey: ['leave', 'approvals', 'pending'],
    queryFn: async () => {
      try {
        const response = await staffAPI.getPendingLeaveApplications(true); // ro_only=true to filter by approval responsibilities
        return response.data?.results || response.data || [];
      } catch (error) {
        // Fallback for admins if direct endpoint fails
        if (error?.response?.status === 403) {
          try {
            const r2 = await staffAPI.getMyTeamLeaves();
            const data2 = r2.data?.results || r2.data || [];
            return (Array.isArray(data2) ? data2 : []).filter(l => (l.leave_status || l.status) === 'submitted');
          } catch (e) {
            return [];
          }
        }
        throw error;
      }
    },
    staleTime: 1 * 60 * 1000, // 1 minute - approvals need to be fresh
  });
};

// Leave mutations
export const useCreateLeaveApplication = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => staffAPI.createLeaveApplication(data),
    onSuccess: () => {
      // Invalidate relevant caches
      queryClient.invalidateQueries({ queryKey: ['leave', 'applications'] });
      queryClient.invalidateQueries({ queryKey: ['leave', 'balances'] });
      queryClient.invalidateQueries({ queryKey: ['leave', 'statistics'] });
      toast.success('Leave application submitted successfully');
    },
    onError: (error) => {
      console.error('Error creating leave application:', error);
      toast.error('Failed to submit leave application');
    },
  });
};

export const useUpdateLeaveApplication = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.updateLeaveApplication(id, data),
    onSuccess: (data, variables) => {
      // Update caches
      queryClient.invalidateQueries({ queryKey: ['leave', 'applications'] });
      queryClient.invalidateQueries({ queryKey: ['leave', 'approvals'] });
      queryClient.invalidateQueries({ queryKey: ['leave', 'balances'] });
      toast.success('Leave application updated successfully');
    },
    onError: (error) => {
      console.error('Error updating leave application:', error);
      toast.error('Failed to update leave application');
    },
  });
};

export const useApproveLeaveApplication = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.approveLeaveApplication(id, data),
    onSuccess: () => {
      // Invalidate all leave-related caches
      queryClient.invalidateQueries({ queryKey: ['leave'] });
      toast.success('Leave application approved successfully');
    },
    onError: (error) => {
      console.error('Error approving leave application:', error);
      toast.error('Failed to approve leave application');
    },
  });
};

export const useRejectLeaveApplication = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, reason }) => staffAPI.rejectLeaveApplication(id, { reason }),
    onSuccess: () => {
      // Invalidate all leave-related caches
      queryClient.invalidateQueries({ queryKey: ['leave'] });
      toast.success('Leave application rejected');
    },
    onError: (error) => {
      console.error('Error rejecting leave application:', error);
      toast.error('Failed to reject leave application');
    },
  });
};

// Department calendar (filtered by user's department)
export const useDepartmentCalendar = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['leave', 'department-calendar', params],
    queryFn: async () => {
      const response = await staffAPI.getDepartmentCalendar(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    keepPreviousData: true, // Prevent flickering when switching scopes
    ...options, // Allow passing additional options like 'enabled'
  });
};

// Company calendar (all departments)
export const useCompanyCalendar = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['leave', 'company-calendar', params],
    queryFn: async () => {
      const response = await staffAPI.getCompanyCalendar(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    keepPreviousData: true, // Prevent flickering when switching scopes
    ...options, // Allow passing additional options like 'enabled'
  });
};

const invalidateHospitalisationCaches = (queryClient) => Promise.all([
  queryClient.invalidateQueries({ queryKey: ['leave'] }),
  queryClient.invalidateQueries({ queryKey: ['attendance'] }),
  queryClient.invalidateQueries({ queryKey: ['payroll'] }),
]);

export const useProvisionalHospitalisationList = (options = {}) => {
  return useQuery({
    queryKey: ['leave', 'hospitalisation', 'provisional'],
    queryFn: async () => {
      const response = await staffAPI.listProvisionalHospitalisation();
      return response.data?.results || [];
    },
    staleTime: 2 * 60 * 1000,
    ...options,
  });
};

export const useCreateProvisionalHospitalisation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => staffAPI.createProvisionalHospitalisation(data),
    onSuccess: async () => {
      await invalidateHospitalisationCaches(queryClient);
      toast.success('Hospitalisation recorded. Attendance is provisionally paid; HL balance is unchanged.');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to record hospitalisation');
    },
  });
};

export const useExtendProvisionalHospitalisation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, data }) =>
      staffAPI.extendProvisionalHospitalisation(applicationId, data),
    onSuccess: async () => {
      await invalidateHospitalisationCaches(queryClient);
      toast.success('Provisional coverage updated.');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to extend coverage');
    },
  });
};

export const useCancelProvisionalHospitalisation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, data }) =>
      staffAPI.cancelProvisionalHospitalisation(applicationId, data),
    onSuccess: async () => {
      await invalidateHospitalisationCaches(queryClient);
      toast.success('Provisional hospitalisation cancelled.');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to cancel');
    },
  });
};

export const usePreviewHospitalisationReconcile = () => {
  return useMutation({
    mutationFn: ({ applicationId, data }) =>
      staffAPI.previewProvisionalHospitalisationReconcile(applicationId, data),
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to preview reconciliation');
    },
  });
};

export const useReconcileProvisionalHospitalisation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, data }) =>
      staffAPI.reconcileProvisionalHospitalisation(applicationId, data),
    onSuccess: async () => {
      await invalidateHospitalisationCaches(queryClient);
      toast.success('Hospitalisation dates reconciled.');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to reconcile');
    },
  });
};

